import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { TimerMode } from '@/components/settings/timer-configuration/types'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { initiateGameExportAPI, ExportGameOptions } from '@/api/room'

interface ExportOptionsModalProps {
  isOpen: boolean
  onClose: () => void
  roomId: string
  isLive: boolean
  gameMode: TimerMode
}

interface OptionState {
  include_players: boolean
  include_teams: boolean
  include_ai_players: boolean
  include_ai_player_threads: boolean
  include_events: boolean
  include_rounds: boolean
  include_communications: boolean
  include_resource_limits: boolean
  include_world_setup: boolean
  include_advisors: boolean
  include_team_assignments: boolean
  include_mcp_config: boolean
  include_team_chats: boolean
  include_conference: boolean
  include_advisor_chats: boolean
  include_adjudication_bot: boolean
  include_adjudication_chats: boolean
  include_decisions: boolean
  include_team_mode_state: boolean
}

const ALL_SELECTED: OptionState = {
  include_players: true,
  include_teams: true,
  include_ai_players: true,
  include_ai_player_threads: true,
  include_events: true,
  include_rounds: true,
  include_communications: true,
  include_resource_limits: true,
  include_world_setup: true,
  include_advisors: true,
  include_team_assignments: true,
  include_mcp_config: true,
  include_team_chats: true,
  include_conference: true,
  include_advisor_chats: true,
  include_adjudication_bot: true,
  include_adjudication_chats: true,
  include_decisions: true,
  include_team_mode_state: true,
}

function buildDefaultOptions(isTeamMode: boolean): OptionState {
  return {
    ...ALL_SELECTED,
    include_team_mode_state: isTeamMode,
  }
}

function applyDependencyRules(opts: OptionState, isTeamMode: boolean): OptionState {
  const result = { ...opts }

  if (!isTeamMode) {
    result.include_team_mode_state = false
  }

  if (!result.include_teams) {
    result.include_team_chats = false
    result.include_team_assignments = false
    result.include_team_mode_state = false
  }

  if (!result.include_players) {
    result.include_conference = false
    result.include_advisor_chats = false
    result.include_ai_player_threads = false
  }

  if (!result.include_advisors) {
    result.include_advisor_chats = false
  }

  if (!(result.include_players && result.include_advisors)) {
    result.include_advisor_chats = false
  }

  if (!(result.include_players && result.include_ai_players)) {
    result.include_ai_player_threads = false
  }

  if (!(result.include_players && result.include_rounds)) {
    result.include_decisions = false
  }

  if (!result.include_adjudication_bot) {
    result.include_adjudication_chats = false
  }

  if (
    !(
      result.include_adjudication_bot &&
      result.include_players &&
      result.include_rounds &&
      result.include_decisions
    )
  ) {
    result.include_adjudication_chats = false
  }

  if (
    !(result.include_teams && result.include_players && result.include_rounds)
  ) {
    result.include_team_mode_state = false
  }

  return result
}

export function ExportOptionsModal({
  isOpen,
  onClose,
  roomId,
  isLive,
  gameMode,
}: ExportOptionsModalProps) {
  const isTeamMode = gameMode === TimerMode.TEAM_MODE
  const [options, setOptions] = useState(() => buildDefaultOptions(isTeamMode))
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setOptions(buildDefaultOptions(isTeamMode))
    }
  }, [isOpen, isTeamMode])

  const effectiveOptions = isLive
    ? { ...ALL_SELECTED, include_team_mode_state: isTeamMode }
    : applyDependencyRules(options, isTeamMode)

  const toggle = (key: keyof OptionState) => {
    if (isLive) return
    setOptions((prev) =>
      applyDependencyRules({ ...prev, [key]: !prev[key] }, isTeamMode)
    )
  }

  const isDisabled = (key: keyof OptionState): boolean => {
    if (isLive) return true
    const withToggled = { ...options, [key]: true }
    const afterRules = applyDependencyRules(withToggled, isTeamMode)
    if (afterRules[key] === false && options[key] === false) return true
    return false
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      const exportOptions: ExportGameOptions = effectiveOptions
      await initiateGameExportAPI(roomId, exportOptions)
      toast.success(
        "Export started. You will be notified when it's ready and the download will begin automatically."
      )
      onClose()
    } catch {
      toast.error('Failed to start export. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  type OptionItem = {
    key: keyof OptionState
    label: string
    description?: string
    secondary?: boolean
  }

  const optionGroups: { heading: string; items: OptionItem[] }[] = [
    {
      heading: 'Teams',
      items: [
        { key: 'include_teams', label: 'Teams' },
        {
          key: 'include_team_chats',
          label: 'Team chats',
          description: 'Requires teams',
          secondary: true,
        },
        {
          key: 'include_team_assignments',
          label: 'Team assignments',
          description: 'Requires teams; links players, advisors, and AI players to teams',
          secondary: true,
        },
        {
          key: 'include_team_mode_state',
          label: 'Team mode state',
          description:
            'Communicators, strategy proposals, votes, and voting status (requires teams, human players, and rounds)',
          secondary: true,
        },
      ],
    },
    {
      heading: 'Human players',
      items: [
        { key: 'include_players', label: 'Human players' },
        {
          key: 'include_conference',
          label: 'Conference',
          description: 'Requires human players',
          secondary: true,
        },
      ],
    },
    {
      heading: 'AI players',
      items: [
        {
          key: 'include_ai_players',
          label: 'AI players (persona, RAG, documents)',
        },
        {
          key: 'include_ai_player_threads',
          label: 'AI Player Chats',
          description: 'Requires human players and AI players',
          secondary: true,
        },
      ],
    },
    {
      heading: 'Advisors',
      items: [
        {
          key: 'include_advisors',
          label: 'Advisors & Event Analysts (with vector embeddings)',
        },
        {
          key: 'include_mcp_config',
          label: 'MCP configuration',
          description: 'Room-level and per-advisor / per-AI MCP servers',
          secondary: true,
        },
        {
          key: 'include_advisor_chats',
          label: 'Advisor chats',
          description: 'Requires human players and advisors',
          secondary: true,
        },
      ],
    },
    {
      heading: 'Scenario & game structure',
      items: [
        { key: 'include_events', label: 'Scenario & events' },
        {
          key: 'include_rounds',
          label: 'Rounds & timers (incl. current round/phase/state)',
        },
        {
          key: 'include_decisions',
          label: 'Player decisions & adjudications',
          description: 'Requires human players and rounds',
          secondary: true,
        },
        { key: 'include_communications', label: 'Communications' },
        { key: 'include_resource_limits', label: 'Resource limits' },
        { key: 'include_world_setup', label: 'World setup' },
      ],
    },
    {
      heading: 'Adjudication',
      items: [
        {
          key: 'include_adjudication_bot',
          label: 'Adjudicator bot config',
        },
        {
          key: 'include_adjudication_chats',
          label: 'Adjudicator chats',
          description:
            'Requires adjudicator bot, human players, rounds, and decisions',
          secondary: true,
        },
      ],
    },
  ]

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='max-w-lg'>
        <DialogHeader>
          <DialogTitle>Export Game</DialogTitle>
        </DialogHeader>

        {isLive && (
          <div className='rounded-md bg-blue-50 border border-blue-200 px-4 py-2 text-sm text-blue-700'>
            This game is live — all items are exported and cannot be deselected.
          </div>
        )}

        <div className='space-y-5 py-2 max-h-[60vh] overflow-y-auto pr-1'>
          {optionGroups.map((group) => (
            <div key={group.heading}>
              <p className='text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2'>
                {group.heading}
              </p>
              <div className='space-y-2'>
                {group.items
                  .filter(
                    (item) =>
                      item.key !== 'include_team_mode_state' || isTeamMode
                  )
                  .map(({ key, label, description, secondary }) => {
                  const checked = effectiveOptions[key]
                  const disabled = isDisabled(key)
                  return (
                    <div
                      key={key}
                      className={`flex items-start gap-3 ${secondary ? 'ml-5' : ''}`}
                    >
                      <input
                        id={key}
                        type='checkbox'
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggle(key)}
                        className='mt-1 h-4 w-4 rounded border-gray-300 accent-blue-700 cursor-pointer disabled:cursor-not-allowed'
                      />
                      <label
                        htmlFor={key}
                        className={`leading-snug cursor-pointer select-none text-sm ${disabled ? 'opacity-50' : ''}`}
                      >
                        {label}
                        {description && (
                          <span className='block text-xs text-slate-400 font-normal'>
                            {description}
                          </span>
                        )}
                      </label>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className='gap-2'>
          <Button variant='outline' onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            className='bg-dark-blue hover:bg-dark-blue-hovered'
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2' />
                Starting…
              </>
            ) : (
              'Export'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
